import { useContext, useEffect } from 'react';

import { useNavigate } from 'react-router';
import { useDebouncedCallback } from 'use-debounce';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';

import {
  Box,
  Button,
  styled as muiStyled,
  styled,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import { visuallyHidden } from '@mui/utils';

import { CreateButton } from '../../../components/buttons';
import { ConfirmDialog } from '../../../components/dialogs/ConfirmDialog';
import { SearchField } from '../../../components/inputs';
import { Pagination } from '../../../components/paginations';
import { AlertSnackbarContext } from '../../../components/snackbars';
import { Spinner } from '../../../components/spinners';
import { Defaults } from '../../../constants';
import { useDeleteTopic } from '../../../lib/topic/hooks';
import useGetAllTopics from '../../../lib/topic/hooks/useGetAllTopics';
import { SortDirection } from '../../../lib/util/helpers';

const TopicsList = () => {
  const navigate = useNavigate();
  const handleAddTopic = () => navigate(`/cms/topics/create`);
  const handleEditTopic = (topicId: number) => navigate(`/cms/topics/${topicId}`);
  const [query, setQuery] = useQueryParams({
    page: NumberParam,
    search: StringParam,
    sort: StringParam,
  });
  const { page, search, sort } = query;
  const searchString = search && { search };
  const debouncedSearchFn = useDebouncedCallback((value: string) => {
    setQuery({ search: value, page: 0 });
  }, Defaults.SEARCH_DEBOUNCE_MS);

  const {
    data: topics,
    isLoading,
    error,
  } = useGetAllTopics({
    page: Number(page) || 0,
    sort: String(sort || ''),
    ...searchString,
    published: true,
  });

  const [, setAlertSnackbar] = useContext(AlertSnackbarContext);

  useEffect(() => {
    let message = '';
    let severity: 'error' | 'success' | 'info' | 'warning' | undefined = 'success';

    if (message !== '') {
      if (severity === 'success') {
        navigate('/cms/topics/suggested');
      }
      setAlertSnackbar({ message, severity });
    }
  }, [navigate, setAlertSnackbar]);

  const setNewSort = (column: string, direction: SortDirection) => {
    const newSort = direction ? `${column} ${direction}` : undefined;
    setQuery({ sort: newSort });
  };

  const setNewPage = (page: number) => setQuery({ page: page - 1 });

  const sortToObj = (sortStr: string | undefined) => {
    const initialValues = { column: '', direction: undefined };
    if (typeof sortStr !== 'string') {
      return initialValues;
    }
    const [column, direction] = sortStr.split(' ');
    if (typeof column !== 'string' || !['asc', 'desc', undefined].includes(direction)) {
      return initialValues;
    }
    return { ...initialValues, column, direction: direction as SortDirection };
  };

  const sortObj = sortToObj(sort ?? '');

  const columns = [
    {
      text: 'Name',
      name: 'topic.title',
    },
    {
      text: 'Actions',
      name: '',
    },
  ];

  const deleteTopic = useDeleteTopic();

  return (
    <Container>
      <FullWidthBar>
        <Typography variant="h5" sx={{ marginRight: '20px' }}>
          Topics
        </Typography>
        <StyledSearchField
          searchString={search ?? ''}
          onSearch={(search: string) => {
            debouncedSearchFn(search);
          }}
        />
        <CreateButton variant="contained" onClick={handleAddTopic}>
          Add New Topic
        </CreateButton>
      </FullWidthBar>

      <TableContainer sx={{ backgroundColor: 'white' }}>
        <Table aria-labelledby="tableTitle">
          <TableHead>
            <PointerTableRow>
              {columns.map((column) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={sortObj.column === column.name}
                    direction={sortObj.direction}
                    onClick={() => {
                      const isAsc = sortObj.column === column.name && sortObj.direction === 'asc';
                      setNewSort(column.name, isAsc ? 'desc' : 'asc');
                    }}
                    component="a"
                  >
                    {column.text}
                    {sortObj.column === column.name ? (
                      <Box component="span" sx={visuallyHidden}>
                        {sortObj.direction === 'desc' ? 'sorted descending' : 'sorted ascending'}
                      </Box>
                    ) : null}
                  </TableSortLabel>
                </TableCell>
              ))}
            </PointerTableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Spinner />
                </TableCell>
              </TableRow>
            )}
            {!isLoading &&
              !error &&
              topics.data &&
              topics.data.map((row: any, index: number) => {
                return (
                  <StyledTableRow key={index} className={row.deleted ? 'deleted-row' : 'row'}>
                    <TableCell>{row.title}</TableCell>
                    <TableCell>
                      <Button onClick={() => handleEditTopic(row.id)} type="button" style={{ textTransform: 'capitalize' }}>
                        Edit
                      </Button>
                      <ConfirmDialog
                        trigger={(open) => (
                          <Button onClick={open} type="button" style={{ textTransform: 'capitalize' }}>
                            Delete
                          </Button>
                        )}
                        title="Delete topic"
                        description="Are you sure you want to delete this topic?"
                        cancelBtnText={'Cancel'}
                        submitBtnText={'Submit'}
                        onConfirm={async (close) => {
                          await deleteTopic.mutate({ topicId: row.id });
                          close();
                        }}
                        disabled={deleteTopic.isLoading}
                        loading={deleteTopic.isLoading}
                      />
                    </TableCell>
                  </StyledTableRow>
                );
              })}
            {topics.data.length === 0 && (
              <StyledTableRow>
                <TableCell colSpan={5} style={{ textAlign: 'center' }}>
                  No topics records found.
                </TableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {topics.total !== null && topics.total !== 0 && (
        <FullWidthBar>
          <Typography variant="subtitle1">Total: {topics.total}</Typography>
          <StyledPagination
            current={Number(page) + 1 || 1}
            total={Math.ceil(topics.total / Defaults.LIST_PAGE_SIZE)}
            onPageChange={(page: number) => setNewPage(page)}
          />
        </FullWidthBar>
      )}
    </Container>
  );
};

const Container = styled('div')({
  padding: '20px',
  backgroundColor: '#eee',
  flex: 1,
  display: 'flex',
  alignItems: 'stretch',
  flexDirection: 'column',
});

const FullWidthBar = styled('div')({
  display: 'flex',
  flexDirection: 'row',
  margin: '8px 0',
  justifyContent: 'space-between',
  alignItems: 'center',
});

const PointerTableRow = styled(TableRow)({
  cursor: 'pointer',
});

const StyledTableRow = muiStyled(PointerTableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

const StyledPagination = styled(Pagination)({
  alignSelf: 'flex-end',
});

const StyledSearchField = styled(SearchField)({
  marginLeft: 'auto',
});

export default TopicsList;
